/**
 * QboPartiesTab — clients ou fournisseurs QuickBooks (copie locale) : recherche, inactifs, solde ; ligne dépliée = factures du tiers.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Search, Truck, Users } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Empty, Pill, errMsg, inputCls, money } from "../../comptabilite/_shared";
import { Num, TH, THead, Table } from "../../comptabilite/reports/_bits";
import { PARTY_CFG, QBO_STATUS, qboBase } from "./_qbo";

function PartyDocs({ masterId, kind, party }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.get(`${qboBase(masterId)}/entities/${kind}/${party.qbo_id}`).then((r) => setDoc(r.data)).catch(() => setDoc({ documents: [] })); }, [masterId, kind, party.qbo_id]);
  if (!doc) return <div className="p-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  const docs = doc.documents || [];
  return (
    <div className="bg-slate-50/60 px-4 py-3 space-y-2 text-xs" data-testid={`qbo-party-docs-${party.qbo_id}`}>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600">
        {party.address && <span>{party.address}</span>}
        {party.tax_number && <span>N° taxes : <strong>{party.tax_number}</strong></span>}
        {party.terms && <span>Conditions : {party.terms}</span>}
        {party.notes && <span className="italic">{party.notes}</span>}
      </div>
      {docs.length === 0 ? <p className="text-slate-400">Aucune facture dans la copie locale.</p> : (
        <Table>
          <THead><TH className="w-24">Date</TH><TH className="w-24">N°</TH><TH className="w-24">Échéance</TH><TH>Mémo</TH><TH right className="w-28">Total</TH><TH right className="w-28">Solde</TH><TH className="w-24">Statut</TH></THead>
          <tbody className="divide-y">
            {docs.map((d) => (
              <tr key={d.qbo_id}>
                <td className="px-3 py-1 font-mono whitespace-nowrap">{d.txn_date}</td><td className="px-3 py-1 font-mono">{d.doc_number || "—"}</td><td className="px-3 py-1 font-mono text-slate-500 whitespace-nowrap">{d.due_date || "—"}</td>
                <td className="px-3 py-1 truncate max-w-xs">{d.memo}</td><Num v={d.total} blankZero={false} /><Num v={d.balance} />
                <td className="px-3 py-1"><Pill cls={QBO_STATUS[d.status]?.cls}>{QBO_STATUS[d.status]?.label || d.status}</Pill></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default function QboPartiesTab({ masterId, kind }) {
  const cfg = PARTY_CFG[kind];
  const [q, setQ] = useState("");
  const [inactive, setInactive] = useState(false);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`${qboBase(masterId)}/entities/${kind}`, { params: { q, include_inactive: inactive || undefined } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); setData({ items: [], totals: {} }); }
  }, [masterId, kind, q, inactive]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const Icon = kind === "customers" ? Users : Truck;
  return (
    <div className="space-y-3" data-testid={`qbo-parties-${kind}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Rechercher un ${cfg.label.toLowerCase()} (nom, courriel, n° taxes)`} className={`${inputCls} pl-9`} data-testid="qbo-parties-search" />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} data-testid="qbo-parties-inactive" /> Inclure les inactifs</label>
        {data && <span className="text-xs text-slate-500 ml-auto" data-testid="qbo-parties-count">{data.totals.count} {cfg.plural.toLowerCase()} · {cfg.balanceLabel} <strong className="font-mono">{money(data.totals.balance)}</strong></span>}
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        {!data ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          : data.items.length === 0 ? <Empty icon={Icon} text={`Aucun ${cfg.label.toLowerCase()} QuickBooks dans la copie locale — cliquez « Actualiser » en haut.`} testId="qbo-parties-empty" />
            : (
              <Table>
                <THead><TH className="w-6"></TH><TH>{cfg.label}</TH><TH className="hidden md:table-cell">Courriel · téléphone</TH><TH className="hidden lg:table-cell w-32">N° taxes</TH><TH className="hidden lg:table-cell w-28">Conditions</TH><TH right className="w-32">{cfg.balanceLabel}</TH><TH className="w-20"></TH></THead>
                <tbody className="divide-y">
                  {data.items.map((p) => (
                    <PartyRow key={p.qbo_id} p={p} open={open === p.qbo_id} toggle={() => setOpen(open === p.qbo_id ? null : p.qbo_id)} masterId={masterId} kind={kind} />
                  ))}
                </tbody>
              </Table>
            )}
      </div>
    </div>
  );
}

function PartyRow({ p, open, toggle, masterId, kind }) {
  return (
    <>
      <tr className="hover:bg-emerald-50/40 cursor-pointer" onClick={toggle} data-testid={`qbo-party-row-${p.qbo_id}`}>
        <td className="pl-3 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="px-3 py-2"><div className="font-medium text-slate-800">{p.name}</div>{p.company && p.company !== p.name && <div className="text-[11px] text-slate-500">{p.company}</div>}</td>
        <td className="px-3 py-2 text-xs text-slate-600 hidden md:table-cell">{p.email}{p.email && p.phone ? " · " : ""}{p.phone}</td>
        <td className="px-3 py-2 text-xs font-mono hidden lg:table-cell">{p.tax_number || "—"}</td>
        <td className="px-3 py-2 text-xs hidden lg:table-cell">{p.terms || "—"}</td>
        <Num v={p.balance} className={p.balance > 0 ? "font-semibold" : ""} />
        <td className="px-3 py-2">{!p.active && <Pill cls="bg-slate-200 text-slate-600 border-slate-300">Inactif</Pill>}</td>
      </tr>
      {open && <tr><td colSpan={7} className="p-0"><PartyDocs masterId={masterId} kind={kind} party={p} /></td></tr>}
    </>
  );
}
